/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import dayjs from 'dayjs';
import _ from 'lodash';

import { REGEXPS } from '@/common'; // Ensure REGEXPS.ISO_DATE is defined correctly here
import { ALL_OPTION_KEY } from '@/common/constants';

const ARRAY_DELIMITER = ',';

function safeClone<T>(obj: T): T {
  try {
    if (typeof structuredClone === 'function') {
      return structuredClone(obj);
    }
  } catch (e) {
    // Fallback or ignore
  }
  if (typeof obj === 'object' && obj !== null) {
    if (Array.isArray(obj)) {
      // @ts-ignore
      return [...obj];
    } else {
      // @ts-ignore
      return { ...obj };
    }
  }
  return obj;
}

function getInitialFiltersFromUrl<T extends Record<string, any>>(search: string, initialValues: T): T {
  const params = new URLSearchParams(search);
  const stateFromUrl = safeClone(initialValues); // Consider _.cloneDeep if deep cloning is needed

  Object.keys(initialValues).forEach((key) => {
    if (params.has(key)) {
      const urlValue = params.get(key);
      const initialValue = initialValues[key];

      if (urlValue === null) return;

      try {
        let valueSet = false;

        // --- Handle Single Date ---
        if (!valueSet && (initialValue instanceof Date || initialValue === null)) {
          if (typeof urlValue === 'string' && REGEXPS.ISO_DATE.test(urlValue)) {
            const date = dayjs(urlValue).toDate();
            if (!isNaN(date.getTime())) {
              stateFromUrl[key as keyof T] = date as T[keyof T];
              valueSet = true;
            }
          }
        }

        // --- Handle Arrays ---
        if (!valueSet && Array.isArray(initialValue)) {
          const stringValues = urlValue
            .split(ARRAY_DELIMITER)
            .map((s) => s.trim())
            .filter((s) => s.length > 0);

          if (stringValues.length > 0) {
            // --- MODIFIED TYPE DETECTION ---
            let isDateArray = initialValue.length > 0 && initialValue[0] instanceof Date;
            if (!isDateArray && initialValue.length === 0 && REGEXPS.ISO_DATE.test(stringValues[0])) {
              isDateArray = true;
            }
            const isNumberArray = initialValue.length > 0 && typeof initialValue[0] === 'number';
            // Add similar check for numbers if needed for empty initial arrays:
            // if (!isNumberArray && initialValue.length === 0 && !isNaN(Number(stringValues[0]))) { isNumberArray = true; }
            const isBooleanArray = initialValue.length > 0 && typeof initialValue[0] === 'boolean';
            // Add similar check for booleans if needed for empty initial arrays:
            // if (!isBooleanArray && initialValue.length === 0 && ['true', 'false'].includes(stringValues[0].toLowerCase())) { isBooleanArray = true; }
            // --- END MODIFIED TYPE DETECTION ---

            let parsedValues: any[] = [];

            if (isDateArray) {
              parsedValues = stringValues.map(val => {
                if (REGEXPS.ISO_DATE.test(val)) { // Keep checking each part
                  const date = dayjs(val).toDate();
                  return !isNaN(date.getTime()) ? date : undefined;
                }
                return undefined;
              }).filter(item => item !== undefined);
            } else if (isNumberArray) {
              parsedValues = stringValues.map(val => {
                const num = Number(val);
                return !isNaN(num) && isFinite(num) ? num : undefined;
              }).filter(item => item !== undefined);
            } else if (isBooleanArray) {
              parsedValues = stringValues.map(val => {
                const lowerVal = val.toLowerCase();
                if (lowerVal === 'true') return true;
                if (lowerVal === 'false') return false;
                return undefined;
              }).filter(item => item !== undefined);
            } else {
              parsedValues = stringValues; // Assign the string values
            }

            // Assign the parsed values if any were successfully parsed or if it was specifically identified type
            if (parsedValues.length > 0 || isDateArray || isNumberArray || isBooleanArray) {
              stateFromUrl[key as keyof T] = parsedValues as T[keyof T];
              valueSet = true;
            }

          } else {
            // URL param was present but empty after split/trim (e.g., ?myArray=,)
            stateFromUrl[key as keyof T] = [] as T[keyof T]; // Ensure it's an empty array
            valueSet = true;
          }
        } // end Array.isArray(initialValue)


        // --- Handle Boolean ---
        if (!valueSet && (typeof initialValue === 'boolean' || initialValue === null)) {
          const lowerVal = urlValue.toLowerCase();
          if (lowerVal === 'true') {
            stateFromUrl[key as keyof T] = true as T[keyof T];
            valueSet = true;
          } else if (lowerVal === 'false') {
            stateFromUrl[key as keyof T] = false as T[keyof T];
            valueSet = true;
          }
        }

        // --- Handle Number ---
        if (!valueSet && (typeof initialValue === 'number' || initialValue === null)) {
          const num = Number(urlValue);
          if (!isNaN(num) && isFinite(num)) {
            stateFromUrl[key as keyof T] = num as T[keyof T];
            valueSet = true;
          }
        }

        // --- Fallback to String ---
        if (!valueSet) {
          stateFromUrl[key as keyof T] = urlValue as T[keyof T];
        }

      } catch (e) {
        stateFromUrl[key as keyof T] = initialValues[key];
      }
    } else {
      stateFromUrl[key as keyof T] = initialValues[key];
    }
  });
  return stateFromUrl;
}


export type FilterSetters<T> = {
  [K in keyof T]: (value: T[K]) => void;
};

interface UseFiltersResult<T> {
  filtersValues: T;
  setFilterValue: FilterSetters<T>;
  clearFilter: (keyToClear: keyof T) => void;
  clearAllFilters: () => void;
}

interface UseFiltersOptions {
  autoClear?: boolean;
}

export function useFilters<T extends Record<string, any>>(
  initialValues: T,
  options?: UseFiltersOptions
): UseFiltersResult<T> {
  const history = useHistory();
  const location = useLocation();
  const isMounted = useRef(false);

  const [filters, setFilters] = useState<T>(() => getInitialFiltersFromUrl(location.search, initialValues));

  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
      return;
    }

    const params = new URLSearchParams(location.search);
    let changed = false;

    Object.keys(initialValues).forEach((key) => {
      const value = filters[key as keyof T] as any;
      const initialValueForKey = initialValues[key];
      let newValueString: string | null = null;

      if (Array.isArray(value)) {
        if (value.length === 1 && value[0] === ALL_OPTION_KEY) {
          newValueString = null;
        } else if (value.length > 0) {
          newValueString = value
            .map((item: any) => {
              if (item instanceof Date && !isNaN(item.getTime())) {
                return item.toISOString();
              }
              return String(item);
            })
            .join(ARRAY_DELIMITER);
        } else {
          newValueString = null;
        }
      } else if (value instanceof Date) {
        if (!isNaN(value.getTime())) {
          newValueString = value.toISOString();
        } else {
          newValueString = null;
        }
      } else if (value !== null && value !== undefined && String(value).length > 0) {
        if (typeof value === 'string' && value === ALL_OPTION_KEY) {
          if (initialValueForKey === ALL_OPTION_KEY) {
            newValueString = String(value);
          } else {
            newValueString = null;
          }
        } else {
          newValueString = String(value);
        }
      }

      const currentValueString = params.get(key);

      if (newValueString !== null) {
        if (currentValueString !== newValueString) {
          params.set(key, newValueString);
          changed = true;
        }
      } else {
        if (params.has(key)) {
          params.delete(key);
          changed = true;
        }
      }
    });

    if (changed) {
      const searchString = params.toString();
      history.replace({
        pathname: location.pathname,
        search: searchString ? `?${searchString}` : '',
      });
    }
  }, [filters, history, location.pathname, location.search, initialValues]);


  const setFilterValue = useMemo(() => {
    const setters = {} as FilterSetters<T>;
    Object.keys(initialValues).forEach((key) => {
      setters[key as keyof T] = (value: T[keyof T]) => {
        let targetValue = value as any;
        const initialValueForKey = initialValues[key as keyof T];
        const isExplicitNullString = typeof value === 'string' && value === ALL_OPTION_KEY;
        const isExplicitNullArray = Array.isArray(value) && value.length === 1 && value[0] === ALL_OPTION_KEY;

        if (isExplicitNullString) {
          targetValue = initialValueForKey;
        } else if (isExplicitNullArray) {
          // Let targetValue remain as the [ALL_OPTION_KEY] array for comparison later
          targetValue = value;
        } else if (Array.isArray(targetValue)) {
          // Filter out null/undefined unless it's the specific ALL_OPTION_KEY array
          targetValue = targetValue.filter((item: any) => item !== null && item !== undefined);
        }

        setFilters((prev) => {
          const previousValueForKey = prev[key as keyof T] as any;

          if (_.isEqual(previousValueForKey, targetValue)) {
            return prev;
          }

          if (
            previousValueForKey instanceof Date && targetValue instanceof Date &&
            previousValueForKey.getTime() === targetValue.getTime()
          ) {
            return prev;
          }

          if (
            Array.isArray(previousValueForKey) && Array.isArray(targetValue) &&
            previousValueForKey.length === targetValue.length && // Check length first
            (previousValueForKey.length === 0 || // Handle empty arrays equality
              (previousValueForKey[0] instanceof Date && targetValue[0] instanceof Date && // Check if potential date arrays
                previousValueForKey.every(
                  (d, i) => d instanceof Date && targetValue[i] instanceof Date && d.getTime() === targetValue[i].getTime()
                )
              )
            )
          ) {
            // This specific check might need adjustment if non-date arrays are compared deeply elsewhere.
            // _.isEqual handles general cases but not Date objects within arrays correctly by default.
            return prev;
          }


          return {
            ...prev,
            [key]: targetValue,
          };
        });
      };
    });
    return setters;
  }, [initialValues]);


  const clearFilter = useCallback(
    (keyToClear: keyof T) => {
      const defaultValue = initialValues[keyToClear] as any;
      setFilters((prev) => {
        const previousValueForKey = prev[keyToClear] as any;

        if (_.isEqual(previousValueForKey, defaultValue)) {
          return prev;
        }

        // Specific check for single Date objects
        if (
          previousValueForKey instanceof Date && defaultValue instanceof Date &&
          previousValueForKey.getTime() === defaultValue.getTime()
        ) {
          return prev;
        }

        // Specific check for arrays potentially containing Date objects
        if (
          Array.isArray(previousValueForKey) && Array.isArray(defaultValue) &&
          previousValueForKey.length === defaultValue.length && // Check length first
          (previousValueForKey.length === 0 || // Handle empty arrays equality
            (previousValueForKey[0] instanceof Date && (defaultValue.length === 0 || defaultValue[0] instanceof Date) && // Check if potential date arrays
              previousValueForKey.every(
                (d, i) => d instanceof Date && defaultValue[i] instanceof Date && d.getTime() === defaultValue[i].getTime()
              )
            )
          )
        ) {
          return prev;
        }

        return {
          ...prev,
          [keyToClear]: defaultValue,
        };
      });
    },
    [initialValues]
  );

  const clearAllFilters = useCallback(() => {
    setFilters(prev => _.isEqual(prev, initialValues) ? prev : initialValues);
  }, [initialValues]);

  const isInitialMountEffectRun = useRef(false); // Renamed for clarity
  useEffect(() => {
    // This effect now correctly handles the autoClear logic on unmount
    return () => {
      // Use the ref to ensure this only runs on actual unmount, not the first render cycle
      if (isInitialMountEffectRun.current && options?.autoClear !== false) {
        const keysJson = JSON.stringify(Object.keys(initialValues).sort());
        const initialKeys = JSON.parse(keysJson) as string[];
        const currentSearch = window.location.search;
        const currentParams = new URLSearchParams(currentSearch);
        let paramsChanged = false;

        initialKeys.forEach((key) => {
          if (currentParams.has(key)) {
            currentParams.delete(key);
            paramsChanged = true;
          }
        });

        if (paramsChanged) {
          const newSearchString = currentParams.toString();
          // Use timeout to allow navigation/state updates to potentially settle
          setTimeout(() => {
            // Check if history still exists (component might unmount fully before timeout)
            if (history && typeof history.replace === 'function') {
              history.replace({
                pathname: window.location.pathname, // Use current window location at time of execution
                search: newSearchString ? `?${newSearchString}` : '',
              });
            }
          });
        }
      }
      // Mark that the initial mount effect cleanup logic has been set up / run once
      isInitialMountEffectRun.current = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options?.autoClear, history]); // Re-run only if autoClear option changes or history instance changes


  return {
    filtersValues: filters,
    setFilterValue,
    clearFilter,
    clearAllFilters,
  };
}

export default useFilters;